import { createRoot } from 'react-dom/client'
import * as Sentry from "@sentry/react"

// Skyline design system — load tokens before everything else
import './styles/tokens.css'
import './styles/base.css'
// Global component styles used via className across the app
import './components/ui/Button.css'
import './components/ui/Badge.css'

import App from './components/App'
import { supabaseClient } from './utils/supabase'
import { registerChunkLoadRecovery } from './utils/chunkLoadRecovery'
import { getBuildId } from './utils/buildInfo'
import { initializeProductAnalytics } from './utils/productAnalytics'
import { startMetadataBoot } from './utils/persistentMetadataCache'

registerChunkLoadRecovery()
initializeProductAnalytics()
startMetadataBoot()

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // REG-963: dev and preview builds reported as production and shared its
  // error quota. Vercel's VERCEL_ENV (production / preview / development).
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
  release: getBuildId(),
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.supabaseIntegration({ supabaseClient }),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      // Additional SDK configuration here
      colorScheme: "system",
      enableScreenshot: true,
      autoInject: false,
      showName: false,
      showEmail: false,
    }),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.supabase\.co/],
  beforeSendSpan(span) {
    // Report identity and timing are useful; filter values and RPC bodies may contain
    // customer data and must never leave the browser as telemetry.
    const data = { ...span.data };
    delete data['db.query'];
    delete data['db.body'];
    delete data['http.request.body'];
    return {
      ...span,
      data,
      description: span.description?.replace(/\?.*$/, ''),
    };
  },
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when an error occurs.
});

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Failed to find the root element')
}

createRoot(rootElement).render(
  // <StrictMode>
    <App />
  // </StrictMode>
)
